import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchProducts } from '../services/api';
import { enrichProduct } from '../data/productExtras';
import { FALLBACK_API_PRODUCTS } from '../data/fallbackProducts';
import { readProductCache, writeProductCache } from '../utils/productCache';

const toEnriched = (rows) => rows.map(enrichProduct);

export const useProducts = (pollInterval = 20_000) => {
  const initialCache = readProductCache({ allowStale: true });
  const [products, setProducts] = useState(() =>
    initialCache?.length ? toEnriched(initialCache) : toEnriched(FALLBACK_API_PRODUCTS)
  );
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [stockSynced, setStockSynced] = useState(Boolean(initialCache?.length));
  const busyRef = useRef(false);
  const productsRef = useRef(products);
  productsRef.current = products;

  const load = useCallback(async (silent = false) => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (silent) {
      setRefreshing(true);
    } else if (productsRef.current.length === 0) {
      setLoading(true);
    }

    try {
      const data = await fetchProducts({ skipCache: true });
      if (Array.isArray(data) && data.length > 0) {
        setProducts(toEnriched(data));
        writeProductCache(data);
        setStockSynced(true);
        setLoadError(false);
      }
    } catch {
      const cached = readProductCache({ allowStale: true });
      if (cached?.length) {
        setProducts(toEnriched(cached));
        setStockSynced(true);
      } else {
        setProducts(toEnriched(FALLBACK_API_PRODUCTS));
      }
      setLoadError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
      busyRef.current = false;
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem('lumora_products_cache_v1');
      localStorage.removeItem('lumora_products_cache_v2');
      localStorage.removeItem('lumora_products_cache_v3');
    } catch {
      /* private mode */
    }
    load(true);

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);

    let interval;
    if (pollInterval > 0) {
      interval = setInterval(refreshIfVisible, pollInterval);
    }

    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
      if (interval) clearInterval(interval);
    };
  }, [load, pollInterval]);

  const getProductBySlug = useCallback(
    (slug) => products.find((p) => p.slug === slug),
    [products]
  );

  const getProductById = useCallback(
    (id) => products.find((p) => String(p.id) === String(id)),
    [products]
  );

  return {
    products,
    loading,
    refreshing,
    loadError,
    stockSynced,
    reload: () => load(false),
    getProductBySlug,
    getProductById,
  };
};
